<template>
  <v-row>
    <v-col cols="12">
      <v-alert type="info" v-if="item.infoText">
        {{ item.infoText || '' }}
      </v-alert>
      <v-alert type="info" dense v-else>
        {{ item.infoText || 'Bitte wähle die Tage und Zeitfenster aus.' }}
        An welchen Tagen hast du im Zeitraum
        vom {{ moment(item.from).format('DD.MM.YYYY') }}
        bis zum {{ moment(item.to).format('DD.MM.YYYY') }}
        Zeit, um an deinen Schulen die Kurse neu vorzustellen? <br>
        <strong>Hinweis: Bitte gib die maximal möglichen Zeitfenster an, in denen du Zeit hast.</strong>
      </v-alert>

      <!-- Tage nach KW gruppiert -->
      <div v-for="group in groupedDays" :key="group.week" class="mt-4">
        <h3 class="subtitle-1">
          <span class="font-weight-medium">KW {{ group.week }}</span>
          {{ moment(group.days[0]).format('DD.MM.YYYY') }}
          -
          {{ moment(group.days[group.days.length - 1]).format('DD.MM.YYYY') }}
        </h3>

        <div
            v-for="day in group.days"
            :key="day"
            class="mb-4 pa-4"
            style="border:1px solid rgba(0,0,0,0.1); border-radius:8px;"
        >
          <v-checkbox
              :label="formatDay(day)"
              :input-value="selectedDays.includes(day)"
              @click="toggleDay(day)"
              :hide-details="true"
              dense
              color="info"
          />

          <!-- Zeitfenster direkt unter der Checkbox -->
          <div v-if="selectedDays.includes(day)" class="mt-3">
            <div
                v-for="(_, idx) in availabilities[day]"
                :key="idx"
                class="mb-4"
            >
              <div class="d-flex align-center mb-2">
                <v-select
                    :items="fromOptions()"
                    v-model="availabilities[day][idx].from"
                    label="Von"
                    dense
                    class="mr-2"
                    color="info"
                    hide-details
                    item-color="info"
                    prepend-icon="mdi-clock-outline"
                />
                <v-select
                    :items="toOptions(day, idx)"
                    v-model="availabilities[day][idx].to"
                    label="Bis"
                    dense
                    class="mr-2"
                    color="info"
                    hide-details
                    item-color="info"
                    prepend-icon="mdi-clock-outline"
                />
              </div>
              <div v-if="!isValidSlot(day, idx)" class="error--text caption">
                Bitte stelle sicher, dass der Slot mindestens zwei Stunden
                zwischen 08:00 und 14:00 Uhr liegt.
              </div>
            </div>
            <!--
            <v-btn small text @click="addTime(day)">
              <v-icon left small>mdi-plus</v-icon>
              Zeitfenster hinzufügen
            </v-btn>
            -->
          </div>
        </div>
      </div>

      <div class="my-4 pa-4" style="border:1px solid rgba(0,0,0,0.1); border-radius:8px;">
        <v-checkbox
            label="Ich kann an keinem dieser Tage"
            v-model="cannotAttend"
            :hide-details="true"
            dense
            color="info"
        />
      </div>


      <!-- Required-Hinweis -->
      <div
          v-if="item.required && selectedDays.length === 0 && !cannotAttend"
          class="error--text caption mt-2"
      >
        Bitte wähle mindestens einen Tag aus.
      </div>

      <v-btn depressed block v-if="step > 1" @click="$emit('update:step', step - 1)">
        Zurück
      </v-btn>

      <v-btn
          block
          class="my-4"
          color="info"
          @click="goNext"
          :disabled="(item.required && selectedDays.length === 0 && !cannotAttend) || hasInvalidSlot"
      >
        Weiter
      </v-btn>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import Vue from 'vue';
import moment from 'moment';
import {LeanDocument} from 'mongoose';
import {SurveyItemType} from '@/enums/survey-manager/SurveyItemType.enum';
import {SurveyItemResponseType} from '@/enums/survey-manager/SurveyItemResponseType.enum';
import {UserSurveyDocument, WeekDaysRange} from '@/documentTypes/survey-manager/UserSurvey.document';
import {
  WeekDaysRangeSurveyDataResponseItem,
} from "@/classes/dto/survey-manager/response/GetSurveyData.response.dto";

moment.locale('de');

export default Vue.extend({
  name: 'WeekDaysRangePicker',
  props: {
    item: {
      type: Object as () => WeekDaysRange & {
        type: SurveyItemType.WEEK_DAYS_RANGE;
        responseType:
            | SurveyItemResponseType.HANDOUT_AVAILABILITIES
            | SurveyItemResponseType.TEXT_ONLY;
      },
      required: true,
    },
    step: {
      type: Number,
      required: true,
    },
    survey: {
      type: Object as () => LeanDocument<UserSurveyDocument>,
      required: true,
    },
    additionalData: {
      type: Object as () => WeekDaysRangeSurveyDataResponseItem,
      required: true,
    },
  },
  data: () => ({
    availableDays: [] as string[],
    selectedDays: [] as string[],
    availabilities: {} as Record<string, Array<{ from: string; to: string }>>,
    cannotAttend: false,
  }),
  computed: {
    moment: () => moment,

    // generates a list of available days based on the item range or additional data
    groupedDays(): Array<{ week: number; days: string[] }> {
      const groups: Record<number, string[]> = {};
      this.availableDays.forEach(d => {
        const w = moment(d).isoWeek();
        if (!groups[w]) groups[w] = [];
        groups[w].push(d);
      });
      return Object.keys(groups)
          .map(w => ({week: +w, days: groups[+w].sort()}))
          .sort((a, b) => a.week - b.week);
    },

    // Prüft, ob es ungültige Slots gibt
    hasInvalidSlot(): boolean {
      if (this.cannotAttend) {
        return false;
      }
      return this.selectedDays.some(day =>
          this.availabilities[day].some((_, idx) => !this.isValidSlot(day, idx)),
      );
    },

    // every quarter between 08:00 to 14:00 in minutes
    timeSlots(): number[] {
      const slots: number[] = [];
      for (let m = 8 * 60; m <= 14 * 60; m += 15) slots.push(m);
      return slots;
    },

    // String representation of time slots
    timeStrings(): string[] {
      return this.timeSlots.map(m => this.minutesToHM(m));
    },
  },
  mounted() {
    this.generateAvailableDays();
  },
  methods: {
    // Toggle-Handler
    toggleDay(day: string) {
      const isOn = !this.selectedDays.includes(day);
      this.onDayChange(day, isOn);
    },
    onDayChange(day: string, isOn: boolean) {
      if (isOn) {
        this.selectedDays = this.item.multi
            ? [...this.selectedDays, day]
            : [day];
        if (!this.availabilities[day]) {
          this.$set(this.availabilities, day, [
            {from: '08:00', to: '12:00'},
          ]);
        }
      } else {
        this.selectedDays = this.selectedDays.filter(d => d !== day);
        this.$delete(this.availabilities, day);
      }
    },
    generateAvailableDays() {
      // serverside filter of possible days excluding weekends and holidays
      if (this.additionalData.possibleDays && this.additionalData.possibleDays.length > 0) {
        this.availableDays = this.additionalData.possibleDays.map(d => moment(d).format('YYYY-MM-DD'));
      }  // if no additional data provided, use the range from item and generate weekdays
      else {
        const start = moment(this.item.from);
        const end = moment(this.item.to);
        const days: string[] = [];
        while (start.isSameOrBefore(end, 'day')) {
          const wd = start.isoWeekday();
          if (wd >= 1 && wd <= 5) days.push(start.format('YYYY-MM-DD'));
          start.add(1, 'day');
        }
        this.availableDays = days;
      }
    },
    formatDay(day: string) {
      return moment(day).format('dddd, DD.MM.YYYY');
    },

    fromOptions(): string[] {
      return this.timeSlots
          .filter(m => m <= 12 * 60)
          .map(m => this.minutesToHM(m));
    },
    toOptions(day: string, idx: number): string[] {
      const from = this.parseHM(this.availabilities[day][idx].from);
      return this.timeSlots
          .filter(m => m >= from + 120 && m <= 14 * 60)
          .map(m => this.minutesToHM(m));
    },
    // checks if a slot is valid
    isValidSlot(day: string, idx: number): boolean {
      const slot = this.availabilities[day][idx];
      const from = this.parseHM(slot.from);
      const to = this.parseHM(slot.to);
      return (
          to - from >= 120 && from >= 8 * 60 && to <= 14 * 60
      );
    },
    // helper functions for time conversion
    minutesToHM(m: number): string {
      const h = Math.floor(m / 60)
          .toString()
          .padStart(2, '0');
      const mm = (m % 60).toString().padStart(2, '0');
      return `${h}:${mm}`;
    },
    parseHM(str: string): number {
      const [h, mm] = str.split(':').map(Number);
      return h * 60 + mm;
    },

    // Emission of the response item and step increment
    goNext() {
      const payload = {
        type: SurveyItemType.WEEK_DAYS_RANGE,
        days: [] as Array<{ from: string; to: string }>,
      };
      if (!this.cannotAttend) {
        this.selectedDays.forEach(day => {
          this.availabilities[day].forEach(slot => {
            if (this.isValidSlot(day, this.availabilities[day].indexOf(slot))) {
              payload.days.push({
                from: moment(`${day}T${slot.from}`).toISOString(),
                to: moment(`${day}T${slot.to}`).toISOString(),
              });
            }
          });
        });
      }
      this.$emit('responseItem', payload);
      this.$emit('update:step', this.step + 1);
    },
  },
  watch: {
    cannotAttend(val: boolean) {
      if (val) {
        this.selectedDays = [];
        this.availabilities = {};
      }
    },
    selectedDays(val: string[]) {
      if (val.length > 0 && this.cannotAttend) {
        this.cannotAttend = false;
      }
    },
  },
});
</script>
